import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react"

export type ConstructorWorkflowWindowId =
  | "plan-review"
  | "plan-voice-fill"
  | "plan-auto-distribute"
  | "plan-voice-composer"
  | "plan-presentation"
  | "plan-tools"

type WindowState = {
  open: boolean
  minimized: boolean
}

type ConstructorWorkflowContextValue = {
  windows: Record<ConstructorWorkflowWindowId, WindowState>
  openWindow: (id: ConstructorWorkflowWindowId) => void
  closeWindow: (id: ConstructorWorkflowWindowId) => void
  minimizeWindow: (id: ConstructorWorkflowWindowId) => void
  restoreWindow: (id: ConstructorWorkflowWindowId) => void
  isWindowOpen: (id: ConstructorWorkflowWindowId) => boolean
}

const defaultWindows = (): Record<ConstructorWorkflowWindowId, WindowState> => ({
  "plan-review": { open: false, minimized: false },
  "plan-voice-fill": { open: false, minimized: false },
  "plan-auto-distribute": { open: false, minimized: false },
  "plan-voice-composer": { open: false, minimized: false },
  "plan-presentation": { open: false, minimized: false },
  "plan-tools": { open: false, minimized: false },
})

const ConstructorWorkflowContext = createContext<ConstructorWorkflowContextValue | null>(null)

export function ConstructorWorkflowProvider({ children }: { children: ReactNode }) {
  const [windows, setWindows] = useState(defaultWindows)

  const patch = useCallback((id: ConstructorWorkflowWindowId, patch: Partial<WindowState>) => {
    setWindows((current) => ({
      ...current,
      [id]: { ...current[id], ...patch },
    }))
  }, [])

  const openWindow = useCallback((id: ConstructorWorkflowWindowId) => {
    setWindows((current) => {
      const next = { ...current }
      if (id !== "plan-tools") {
        next["plan-tools"] = { ...next["plan-tools"], open: false, minimized: false }
      }
      next[id] = { ...next[id], open: true, minimized: false }
      return next
    })
  }, [])

  const closeWindow = useCallback((id: ConstructorWorkflowWindowId) => {
    patch(id, { open: false, minimized: false })
  }, [patch])

  const minimizeWindow = useCallback((id: ConstructorWorkflowWindowId) => {
    patch(id, { minimized: true })
  }, [patch])

  const restoreWindow = useCallback((id: ConstructorWorkflowWindowId) => {
    patch(id, { minimized: false, open: true })
  }, [patch])

  const isWindowOpen = useCallback(
    (id: ConstructorWorkflowWindowId) => windows[id]?.open === true && windows[id]?.minimized !== true,
    [windows],
  )

  const value = useMemo(
    () => ({ windows, openWindow, closeWindow, minimizeWindow, restoreWindow, isWindowOpen }),
    [windows, openWindow, closeWindow, minimizeWindow, restoreWindow, isWindowOpen],
  )

  return <ConstructorWorkflowContext.Provider value={value}>{children}</ConstructorWorkflowContext.Provider>
}

export function useConstructorWorkflow() {
  const ctx = useContext(ConstructorWorkflowContext)
  if (!ctx) {
    throw new Error("useConstructorWorkflow must be used within ConstructorWorkflowProvider")
  }
  return ctx
}
